<template>
	<ModalHeader ref="header" @click-button="clickSave" @switched="changeCompositeStatus" with-switch :title="getString('x_variations', [menuItem.name])" :button-text="getString('save')" :active="menuItem.composite" />
	<div class="pb-2 px-2">
		<AppSortableList @sorting="startSorting" @sorted="changeListsOrder" @add-item="createNewList" full-width ref="composite-lists" class="composite-sortable-list" key-prop="tempid" create-dropdown-on-expand :items="itemListsArray">
			<template v-slot:title-controls="{item}">
				<div class="col-12" @click.stop="clickEditList(item.tempid)">
					<div class="row composite-list-item">
						<img class="handle icon-dimension-20" :class="{undraggable: item.active}" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" :title="getString('drag_to_sort')" draggable="false">
						<p class="composite-list-item-title">{{ item.name }}</p>
						<AppButtonIcon :ref="'button-icon-'+item.tempid" :active="item.active" :title="getString('edit')" icon-name="icon-edit-white" class="edit-composite-list-btn" />
						<AppClickableIcon class="trash-icon" icon-name="iconBin" :auto-set-active="false" :title="getString('delete')" @click.stop="clickDeleteList(item.tempid)" />
					</div>
				</div>
			</template>
			<template v-slot:dropdown-content="{item}">
				<AppTabsSwitch :items="listSectionsTitles" @switched="changePage(item.tempid)" :ref="'switch-'+item.tempid" class="mb-10" />
				<AppPagination class="pagination" change-height hide-dots hide-header transparent :ref="'pagination-'+item.tempid" :pages="listSections">
					<template v-slot:options="{settingItem}">
						<div class="col-12 centered pagination-item-content">
							<CompositeEditorOptions :categoryTempId="categoryTempId" :itemTempId="itemTempId" :listTempId="item.tempid" />
						</div>
					</template>
					<template v-slot:add="{settingItem}">
						<div class="col-12 centered pagination-item-content">
							<CompositeEditorAdd :categoryTempId="categoryTempId" :itemTempId="itemTempId" :listTempId="item.tempid" @added-item="switchToSummary(item.tempid)" />
						</div>
					</template>
					<template v-slot:summary="{settingItem}">
						<div class="col-12 centered pagination-item-content">
							<CompositeEditorSummary :categoryTempId="categoryTempId" :itemTempId="itemTempId" :listTempId="item.tempid" @add-new-product="switchToAddNew(item.tempid)" />
						</div>
					</template>
				</AppPagination>
			</template>
		</AppSortableList>
	</div>
</template>

<script>
import AppPagination from "@/shared/components/AppPagination.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import AppSortableList from "@/shared/components/AppSortableList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppList from "@/shared/components/AppList.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CompositeEditorOptions from "@/console-new/components/config/menu/CompositeEditorOptions.vue";
import CompositeEditorAdd from "@/console-new/components/config/menu/CompositeEditorAdd.vue";
import CompositeEditorSummary from "@/console-new/components/config/menu/CompositeEditorSummary.vue";
import {useUserStore} from "@/console-new/stores";

import {useMenuStore} from "@/console-new/stores";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		return {getString: languageStore.getString, menuStore, showNotification: notificationStore.showNotification, userStore};
	},
	name: "CompositeEditor",
	components: {AppPagination, AppButton, AppSortableList, AppButtonIcon, AppClickableIcon, AppTabsSwitch, AppList, ModalHeader, CompositeEditorOptions, CompositeEditorAdd, CompositeEditorSummary},
	emits: ["save"],
	props: {
		categoryTempId: {
			type: String,
			required: true
		},
		itemTempId: {
			type: String,
			required: true
		},
	},
	data() {
		return {
			loading: false,
			listSections: [
				{id: 'options', slotName: 'options'},
				{id: 'add', slotName: 'add'},
				{id: 'summary', slotName: 'summary'}
			],
			listSectionsTitles: [this.getString('options'), this.getString('add'), this.getString('summary')],
			sorting: false,
		}
	},
	computed: {
		menuItem() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		menuItemNonDeletedLists() {
			let initialLists = this.menuItem?.lists || {};
			for(let list in initialLists) {
				if(initialLists[list].deleted_list) {
					delete initialLists[list];
				}
			}
			return initialLists;
		},
		itemListsArray() {
			let returning = [];
			Object.entries(this.menuItem.lists).forEach(([key, list]) => {
				if(!list.deleted_list) { returning.push(list); }
			});
			return returning;
		},
	},
	methods: {
		changePage(tempId) {
			let newPos = this.$refs['switch-'+tempId].currentActiveItem;
			this.$refs['pagination-'+tempId].changeSlide(newPos);
		},
		switchToAddNew(tempId) {
			this.$refs['switch-'+tempId].clickItem(1);
		},
		switchToSummary(tempId) {
			this.$refs['switch-'+tempId].clickItem(2);
		},
		clickEditList(tempid) {
			if(this.sorting) { return; }
			this.itemListsArray.forEach(list => {
				this.$refs['button-icon-'+list.tempid].isActive = list.tempid === tempid ? !this.menuItemNonDeletedLists[list.tempid].active : false;
			});
			let currentIndex = Object.keys(this.menuItemNonDeletedLists).indexOf(tempid);
			this.$refs['composite-lists'].toggleDropdownDelayed(currentIndex);
		},
		clickDeleteList(tempId) {
			this.menuItem.lists[tempId].deleted_list = true;
		},
		createNewList() {
			let newList = {
				tempid: getRandomAlphanumericString(10),
				active: true,
				collapsedDelayed: false,
				customSlot: 'title-controls',
				dropdownSlot: 'dropdown-content',
				headerSlot: 'controls',
				menu_item_list_id: null,
				title: '',
				name: this.getString('new_list'),
				select_min: 0,
				select_max: 0,
				select_multi: false,
				order_number: this.itemListsArray.length || 0,
				deleted_list: false
			};
			this.itemListsArray.forEach(list => {
				this.$refs['button-icon-'+list.tempid].isActive = false;
			});
			this.$refs['composite-lists'].toggleDropdownDelayed(newList.tempid);
			this.menuItem.lists[newList.tempid] = newList;
		},
		startSorting() {
			this.sorting = true;
		},
		changeListsOrder(evt, newOrder) {
			for(let pos = 0; pos < newOrder.length; pos++) {
				if(newOrder[pos]) {
					let listTempId = newOrder[pos].tempid;
					this.menuItem.lists[listTempId].order_number = pos;
				}
			}
			this.sorting = false;
		},
		saveLists() {
			this.menuStore.updateProductLists(this.menuItem.menu_item_id, this.itemListsArray, (newListArray, details) => {
				if(!newListArray) {
					if(details === 'auth_denied') {
						this.showNotification("auth-denied-error");
					} else {
						this.showNotification("generic-fail");
					}
				} else {
					this.showNotification("composed-saved-lists");
				}
				this.loading = false;
				this.$emit("save");
			});
		},
		clickSave() {
			if(this.loading) { return }
			this.loading = true;
			this.$refs['header'].$el.classList.add('no-pointer-events');
			this.saveLists();
		},
		changeCompositeStatus(newStatus) {
			this.menuItem.composite = newStatus;
		},
	},
}
</script>

<style lang="scss" scoped>
.icon-dimension-20 {
	width: 20px;
	height: 20px;
}
.sheet-title {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
	background-color: var(--color-bg-primary-2);
	border-radius: 13px;
	padding: 10px;
	box-shadow: $box_shadow_inset;
}
.composite-list-item-title {
	margin-right: auto;
	max-width: calc(100% - 95px);

	@media (max-width: 560px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
}
.edit-composite-list-btn {
	margin-right: 15px;
}
.composite-sortable-list:deep(.list-item:not(.list-item-add)) {
	padding: 0;
}
.composite-list-item {
	@include centered;
	@include justifyFlex(between);
	cursor: pointer;
	padding: 10px;

	img {
		transition: opacity 0.3s ease;

		&.undraggable {
			cursor: default;
			pointer-events: none;
			opacity: 0.4;
		}
	}
}
.pagination {
	.page-content {
		min-height: unset;
	}
}
</style>